import { makeAutoObservable, runInAction } from 'mobx'

import { restClient } from '@/Core'
import { serverMessage } from '@/Ship/Helpers/serverError'

import type { CrmContactRow } from './CrmContact'

/** Контактів у одного власника одиниці; з великим запасом — одним запитом. */
export const CRM_CONTACTS_LIMIT = 100

type CollectionResponse =
    | { items?: CrmContactRow[]; 'hydra:member'?: CrmContactRow[]; meta?: { total?: number } }
    | CrmContactRow[]
    | null

/**
 * Вкладка «Контакти» картки власника (`SPEC-008` §7.1): невидалені контакти
 * одного клієнта чи перевізника, новіші першими (так віддає бек).
 *
 * Відбирає записи префільтром `<поле власника>.uuid` спільної колекції
 * (`/services/client_contacts`) — окремого ендпойнта на власника немає.
 * Права рядка — `_permissions` із `?permissions=true`.
 */
export class CrmContactsTabStore {
    items: CrmContactRow[] = []
    /** Скільки всього: якщо більше, ніж показано, вкладка про це скаже */
    total = 0
    isLoading = false
    failed = false
    removingUuid: string | null = null

    constructor(
        private readonly restUrl: string,
        private readonly ownerField: string
    ) {
        makeAutoObservable(this, {}, { autoBind: true })
    }

    /**
     * `forceRefresh`: ядровий клієнт дедуплікує GET на 30 с, і без нього
     * щойно доданий контакт не з'явився б у списку.
     */
    async load(ownerUuid: string | undefined): Promise<void> {
        if (!ownerUuid) return

        this.isLoading = true
        this.failed = false

        try {
            const filters = JSON.stringify({ and: [{ field: `${this.ownerField}.uuid`, op: '=', value: ownerUuid }] })
            const raw = await restClient.get(
                `${this.restUrl}?limit=${CRM_CONTACTS_LIMIT}&permissions=true&filters=${encodeURIComponent(filters)}`,
                undefined,
                { forceRefresh: true }
            )
            const data = raw as unknown as CollectionResponse
            const items = Array.isArray(data) ? data : (data?.items ?? data?.['hydra:member'] ?? [])
            const rows = Array.isArray(items) ? items.filter((item) => typeof item?.uuid === 'string') : []

            runInAction(() => {
                this.items = rows
                this.total = !Array.isArray(data) && typeof data?.meta?.total === 'number' ? data.meta.total : rows.length
            })
        } catch {
            runInAction(() => {
                this.items = []
                this.total = 0
                this.failed = true
            })
        } finally {
            runInAction(() => {
                this.isLoading = false
            })
        }
    }

    /**
     * Видаляє контакт (м'яко, `SPEC-008` §5.2) і перечитує список.
     * Підтвердження — справа вкладки (`Popconfirm`). Повертає текст помилки
     * або `null`.
     */
    async remove(uuid: string, ownerUuid: string): Promise<string | null> {
        if (this.removingUuid !== null) return null

        this.removingUuid = uuid

        try {
            await restClient.delete(`${this.restUrl}/${encodeURIComponent(uuid)}`)
            await this.load(ownerUuid)

            return null
        } catch (error) {
            return serverMessage(error, 'Зверніться до адміністратора системи.')
        } finally {
            runInAction(() => {
                this.removingUuid = null
            })
        }
    }
}
